import { useCallback, useState } from "react";
import { useProfile } from "@ludens-reklame/rubics-v3-react/profile/hooks";
import { EditableProfile } from "@ludens-reklame/rubics-v3-sdk/profile/types";
import { Card, InputGrid } from "../styles.js";
import { RichText } from "../../../internal-components/RichText.js";
import { DefinitionList } from "../../../internal-components/DefinitionList.js";
import { Button } from "../../../internal-components/Button.js";
import { Input } from "../../../internal-components/Input/Input.js";
import { Busy } from "../../../internal-components/Busy.js";

interface Props {
  text?: string;
}

export const Home: React.FC<Props> = ({ text }) => {
  const [editing, setEditing] = useState<boolean>(false);
  const [input, setInput] = useState<Partial<EditableProfile>>({});
  const profileInterface = useProfile();
  const profile = profileInterface.profile;
  const update = profileInterface.update;

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      await update(input);
      setEditing(false);
    },
    [update, input]
  );

  if (!profile) {
    return null;
  }

  return (
    <>
      <Card>
        <h2 className="h3">
          {profile.fullName ? `Heisann, ${profile.fullName}!` : "Heisann!"}
        </h2>
        {profile.email && <p>E-postadresse: {profile.email}</p>}
        {text && <RichText dangerouslySetInnerHTML={{ __html: text }} />}
      </Card>
      <Card>
        {editing ? (
          <>
            <h2 className="h3">Her kan du endre din personlige informasjon</h2>
            <Busy $busy={profileInterface.networking}>
              <form onSubmit={handleSubmit}>
                <InputGrid>
                  <Input
                    label="Fornavn"
                    autoComplete="given-name"
                    required
                    placeholder="Ola"
                    value={input.firstName || profile.firstName || ""}
                    onChange={(e) =>
                      setInput({ ...input, firstName: e.target.value })
                    }
                  />
                  <Input
                    label="Etternavn"
                    autoComplete="family-name"
                    required
                    placeholder="Nordmann"
                    value={input.lastName || profile.lastName || ""}
                    onChange={(e) =>
                      setInput({ ...input, lastName: e.target.value })
                    }
                  />
                  <Input
                    label="E-postadresse"
                    type="email"
                    autoComplete="email"
                    required
                    placeholder="eksempel@epost.no"
                    value={input.email || profile.email || ""}
                    onChange={(e) =>
                      setInput({ ...input, email: e.target.value })
                    }
                  />
                  <Input
                    label="Adresse"
                    placeholder="Adresseveien 1"
                    autoComplete="street-address"
                    value={input.addressLine1 || profile.addressLine1 || ""}
                    onChange={(e) =>
                      setInput({ ...input, addressLine1: e.target.value })
                    }
                  />
                  <Input
                    label="Postnummer"
                    placeholder="0123"
                    autoComplete="postal-code"
                    value={input.postcode || profile.postcode || ""}
                    onChange={(e) =>
                      setInput({ ...input, postcode: e.target.value })
                    }
                  />
                  <Input
                    label="Sted"
                    placeholder="Oslo"
                    autoComplete="address-level2"
                    value={input.city || profile.city || ""}
                    onChange={(e) =>
                      setInput({ ...input, city: e.target.value })
                    }
                  />
                  <Input
                    label="Telefonnummer"
                    placeholder="123 45 678"
                    autoComplete="tel-local"
                    value={input.phone || profile.phone || ""}
                    onChange={(e) =>
                      setInput({ ...input, phone: e.target.value })
                    }
                  />
                </InputGrid>
                <Button
                  type="submit"
                  $variant="secondary"
                  disabled={profileInterface.networking}
                >
                  {profileInterface.networking ? "Lagrer…" : "Lagre endringer"}
                </Button>
              </form>
            </Busy>
          </>
        ) : (
          <>
            <h2 className="h3">Dine personopplysninger</h2>
            <DefinitionList $alternative>
              <dt>Navn</dt>
              <dd>{profile.fullName || "Mangler navn"}</dd>
              <dt>E-post</dt>
              <dd>{profile.email || "Mangler e-postadresse"}</dd>
              <dt>Adresse</dt>
              {!profile.addressLine1 && !profile.postcode && !profile.city ? (
                <dd>Mangler adresse</dd>
              ) : (
                <>
                  {profile.addressLine1 && <dd>{profile.addressLine1}</dd>}
                  {(profile.postcode || profile.city) && (
                    <dd>
                      {profile.postcode} {profile.city}
                    </dd>
                  )}
                </>
              )}
              <dt>Telefonnummer</dt>
              <dd>{profile.phone || "Mangler telefonnummer"}</dd>
            </DefinitionList>
            <Button $variant="secondary" onClick={() => setEditing(true)}>
              Rediger
            </Button>
          </>
        )}
      </Card>
    </>
  );
};
